import { BreakpointObserver } from "@angular/cdk/layout";
import { DOCUMENT, NgTemplateOutlet, AsyncPipe } from "@angular/common";
import { AfterViewInit, Component, ElementRef, inject, Inject, Injector, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { MatSidenavContent, MatSidenavContainer, MatSidenav } from "@angular/material/sidenav";
import { filter } from "rxjs/operators";

import { combineLatest, Observable, Subscription, timer, of } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { AktuelBrugerObserver, Bruger } from "../bruger";
import { UmbracoIndstillinger, UmbracoIndstillingerObserver } from "../umbraco-indstillinger";
import { AppShellScrollService } from "./app-shell-scroll.service";
import { AppShellSettings } from "./app-shell-settings.model";
import { RolleBodyClassService } from "../util/rolle-body-class.service";
import { isTeknikerContext } from "../util";
import { MatIcon } from "@angular/material/icon";
import { DffedbDriftsstatusKnapComponent } from "../../../../web-components/src/lib/dffedb-driftsstatus-knap/dffedb-driftsstatus-knap.component";
import { EforsyningSidenavMenuComponent } from "../eforsyning-menu/eforsyning-sidenav-menu/eforsyning-sidenav-menu.component";
import { ForsyningwebSidenavMenuComponent } from "../eforsyning-menu/forsyningweb-sidenav-menu/forsyningweb-sidenav-menu.component";
import { UlaesteDokumenterService as UlaesteDokumenterService } from "../edh";
import { BottomNavigation } from "../service-pro/bottom-navigation/bottom-navigation";
import { TopNavigation } from "../service-pro/top-navigation/top-navigation";
import { NotificationCenterComponent } from "../push-notification/notification-center.component";
import { FeatureFlagService } from "@e-forsyning/features/feature-flag";

import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "./app-shell-layout.token";

@Component({
    selector: "app-shell",
    templateUrl: "./app-shell.component.html",
    styleUrls: ["./app-shell.component.scss"],
    imports: [
        NgTemplateOutlet,
        MatIcon,
        DffedbDriftsstatusKnapComponent,
        MatSidenavContainer,
        MatSidenav,
        EforsyningSidenavMenuComponent,
        ForsyningwebSidenavMenuComponent,
        MatSidenavContent,
        NotificationCenterComponent,
        AsyncPipe,
        BottomNavigation,
        TopNavigation
    ]
})
export class AppShellComponent implements AfterViewInit {
    private readonly layoutOptions: AppShellLayoutOptions = inject(APP_SHELL_LAYOUT_OPTIONS, { optional: true }) ?? {};

    public settings$: Observable<AppShellSettings>;
    public indstillinger$: Observable<UmbracoIndstillinger>;
    public aktuelBruger$: Observable<Bruger>;
    public navbarMobileHeight$ = new Observable<number>();
    public scrollSubscription: Subscription;

    public scrollPositionTop = true;
    public fofBruger = false;
    public isTeknikerMode = false;
    /** True når vi viser login-siden – top/menu skal kun skjules her. */
    public isLoginPage = false;

    /** Når true: skjul Forsyning|Web (forbruger-)menuen (fx servicepro). */
    public get hideForsyningWebMenu(): boolean {
        return !!this.layoutOptions.hideForsyningWebMenu;
    }

    /** ServicePro top-/bund-navigation — ikke eForsyning tekniker-login (der bruges sidemenu). */
    public get useServiceProNavigation(): boolean {
        return !!this.layoutOptions.teknikerLoginRoute;
    }

    public burgerMenuBadge$: Observable<number | null>;
    public eforsyningIcon$: Observable<string>;

    /** Pilot-forsyning: vis notifikationscenter m.m. Opdateres når AppComponent har modtaget isPilot. */
    public featureFlagEnabled$: Observable<boolean> = inject(FeatureFlagService).isFeatureEnabledObservable();

    // Observable for burger menu badge

    @ViewChild(MatSidenavContent)
    public set setMatSidenavContent(value: MatSidenavContent) {
        this.scrollService.init(value);

        if (this.scrollSubscription) {
            this.scrollSubscription.unsubscribe();
        }
        if (!value) return;
        this.scrollSubscription = value.elementScrolled().subscribe(() => {
            const isAtTop = value.measureScrollOffset("top") < 120;
            this.scrollPositionTop = isAtTop;
        });
    }

    @ViewChild("navbarMobile")
    public navbarMobile: ElementRef;

    constructor(
        breakpointObserver: BreakpointObserver,
        @Inject(DOCUMENT) private document: Document,
        indstillingerObserver: UmbracoIndstillingerObserver,
        private readonly aktuelBrugerObserver: AktuelBrugerObserver,
        private readonly scrollService: AppShellScrollService,
        private readonly rolleBodyClassService: RolleBodyClassService,
        private readonly ulaesteDokumenterService: UlaesteDokumenterService,
        private readonly router: Router
    ) {
        this.indstillinger$ = indstillingerObserver.valueChanges;
        this.aktuelBruger$ = this.aktuelBrugerObserver.valueChanges;
        const breakpointChange$ = breakpointObserver.observe(["(min-width: 768px)"]);
        this.settings$ = combineLatest([this.indstillinger$, breakpointChange$]).pipe(
            map(([indstillinger, state]) => ({
                isDesktop: state.matches,
                isMobile: !state.matches,
                visDriftsstatus: indstillinger.visDriftsstatus,
                driftsstatusForstyrrelserTekst: indstillinger.driftsstatusForstyrrelserTekst,
                driftsstatusIngenForstyrrelserTekst: indstillinger.driftsstatusIngenForstyrrelserTekst
            }))
        );

        this.isTeknikerMode = isTeknikerContext();
        this.updateIsLoginPage(this.router.url);
        this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => this.updateIsLoginPage(e.urlAfterRedirects));
        this.burgerMenuBadge$ = this.isTeknikerMode
            ? of(null)
            : this.ulaesteDokumenterService.hentAntalUlaesteDokumenter().pipe(map((antal) => (antal > 0 ? antal : null)));
        this.eforsyningIcon$ = this.aktuelBrugerObserver.valueChanges.pipe(map((b) => (b && "lock-open") || "lock"));
    }

    private updateIsLoginPage(url: string): void {
        const path = url.replace(/^#/, "").split("?")[0].replace(/^\/+/, "");
        const extras = this.layoutOptions.extraLoginRoutePaths ?? [];
        this.isLoginPage = extras.includes(path) || path === "login" || path.startsWith("login/") || path === "tekniker-login" || path.startsWith("tekniker-login/");
    }

    public toggleAriaExpanded(id: HTMLElement, menuType: string) {
        id.attributes.getNamedItem("aria-expanded").value = id.attributes.getNamedItem("aria-expanded").value === "true" ? "false" : "true";
        id.attributes.getNamedItem("aria-label").value = id.attributes.getNamedItem("aria-expanded").value === "true" ? "Luk " + menuType : "Åben " + menuType;
    }

    public ngAfterViewInit(): void {
        // Ville gerne sætte højden i html til [style.top.px]="navbarMobile.offsetHeight"
        // Det giver imidlertid fejlen: ExpressionChangedAfterItHasBeenCheckedError
        this.navbarMobileHeight$ = timer(0, 100).pipe(
            map(() => this.navbarMobile.nativeElement.offsetHeight),
            distinctUntilChanged()
        );

        this.aktuelBrugerObserver.valueChanges.subscribe((b) => (this.fofBruger = (b && b.rolle && b.rolle === 9) || false));
    }

    public skipToContent(): void {
        const mainContent = this.document.getElementById("main");
        if (!mainContent) return;

        const firstFocusable = this.findFirstFocusableElement(mainContent);
        if (firstFocusable) {
            firstFocusable.focus();
            return;
        }

        mainContent.setAttribute("tabindex", "-1");
        mainContent.focus();
    }

    private findFirstFocusableElement(container: HTMLElement): HTMLElement | null {
        const selector = [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            "[tabindex]:not([tabindex='-1'])"
        ].join(",");

        const elements = Array.from(container.querySelectorAll<HTMLElement>(selector));
        return elements.find((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true") ?? null;
    }

    public bottomNavigationLinks: { label: string; icon: string; route: string; id?: string }[] = [
        { label: "Min side", icon: "list", route: "/min-side", id: "min-side" },
        { label: "Søg", icon: "search", route: "/install-picker", id: "soeg" },
        { label: "Oversigtskort", icon: "map", route: "/oversigtskort", id: "oversigt" }
    ];
}
